/**
 * Visões salvas — o combinado de filtros que a pessoa usa toda manhã.
 *
 * Regra 5 da padronização: *toda lista tem filtro no cabeçalho e visões salvas.
 * Ninguém remarca os mesmos seis filtros toda manhã.*
 *
 * Ficam em `localStorage`, por tela e por projeto. Não vão para o servidor de
 * propósito: são preferência de quem senta na máquina, mudam o tempo todo, e
 * cada gravação no servidor seria uma escrita a mais num banco no limite. O dia
 * em que precisarem ser compartilhadas entre pessoas, vira tabela — e aí a
 * conversa é outra.
 *
 * O armazenamento pode simplesmente não existir (aba anônima, site data
 * bloqueado). Tudo aqui trata isso como "não há visão nenhuma" e segue.
 */

export type Visao<F> = {
  id: string
  nome: string
  filtro: F
  /** Marcada como a que abre por padrão ao entrar na tela. */
  padrao?: boolean
}

const PREFIXO = "sistema-oc.visoes"

function chave(tela: string, escopo: string | number): string {
  return `${PREFIXO}.${tela}.${escopo}`
}

export function lerVisoes<F>(tela: string, escopo: string | number): Visao<F>[] {
  try {
    const cru = localStorage.getItem(chave(tela, escopo))
    if (!cru) return []
    const v = JSON.parse(cru)
    return Array.isArray(v) ? (v as Visao<F>[]) : []
  } catch {
    return []
  }
}

function gravar<F>(tela: string, escopo: string | number, visoes: Visao<F>[]): Visao<F>[] {
  try {
    localStorage.setItem(chave(tela, escopo), JSON.stringify(visoes))
  } catch {
    /* sem armazenamento: a visão vale só enquanto a aba estiver aberta */
  }
  return visoes
}

export function salvarVisao<F>(
  tela: string, escopo: string | number, nome: string, filtro: F,
): Visao<F>[] {
  const limpo = nome.trim()
  if (!limpo) return lerVisoes<F>(tela, escopo)

  const atuais = lerVisoes<F>(tela, escopo)
  // Mesmo nome sobrescreve, em vez de criar uma segunda com o nome igual: quem
  // digita o nome de novo está corrigindo, não colecionando.
  const sem = atuais.filter((v) => v.nome.toLowerCase() !== limpo.toLowerCase())
  return gravar(tela, escopo, [...sem, { id: crypto.randomUUID(), nome: limpo, filtro }])
}

export function removerVisao<F>(tela: string, escopo: string | number, id: string): Visao<F>[] {
  return gravar(tela, escopo, lerVisoes<F>(tela, escopo).filter((v) => v.id !== id))
}

/** Alterna a marca de padrão. Só uma por tela: marcar uma desmarca a anterior. */
export function alternarPadrao<F>(tela: string, escopo: string | number, id: string): Visao<F>[] {
  const atuais = lerVisoes<F>(tela, escopo)
  const alvo = atuais.find((v) => v.id === id)
  const ligando = !alvo?.padrao
  return gravar(tela, escopo, atuais.map((v) => ({ ...v, padrao: ligando && v.id === id })))
}

export function visaoPadrao<F>(tela: string, escopo: string | number): Visao<F> | undefined {
  return lerVisoes<F>(tela, escopo).find((v) => v.padrao)
}

/** Conta quantos campos do filtro estão preenchidos — o "3 filtros" do chip. */
export function contarFiltros(filtro: Record<string, unknown>, ignorar: string[] = []): number {
  return Object.entries(filtro).filter(([k, v]) => {
    if (ignorar.includes(k)) return false
    if (v == null || v === "") return false
    if (Array.isArray(v)) return v.length > 0
    return true
  }).length
}
